import { useState } from 'react'
import { useEffect } from 'react'
import { Shuffle, ShieldOff } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { useUserRole } from '@/hooks/useUserRole'
import { platformApi } from '../api'
import type { SubmissionDetail, Workflow } from '../types'

const MIN_DECLINE = 1
const MIN_REVOKE = 10

const failed = (e: unknown, fallback: string) => toast.error(e instanceof Error ? e.message : fallback)

/** Shown to the requester while an approver's conditions wait for their answer. */
export function ConditionsPanel({ detail, onDone }: { detail: SubmissionDetail; onDone: () => void }) {
    const [comment, setComment] = useState('')
    const [busy, setBusy] = useState<'accept' | 'decline' | null>(null)
    const { userId } = useUserRole()
    const s = detail.submission
    const step = detail.steps.find((x) => x.status === 'awaiting_requester')
    // Only the requester answers conditions; everyone else just sees the step waiting.
    if (!step || s.status !== 'in_review' || s.submitter_id !== userId) return null
    const conditions = [...detail.decisions].reverse()
        .find((d) => d.step === step.step && d.round === step.round && d.decision === 'approve_with_conditions')

    const respond = async (response: 'accept' | 'decline') => {
        if (response === 'decline' && comment.trim().length < MIN_DECLINE) {
            toast.error('Say why you are declining the conditions.')
            return
        }
        setBusy(response)
        try {
            await platformApi.respondConditions(s.id, step.step, response, comment.trim())
            toast.success(response === 'accept' ? 'Conditions accepted' : 'Conditions declined')
            setComment('')
            onDone()
        } catch (e) {
            failed(e, 'Could not record your answer')
        } finally {
            setBusy(null)
        }
    }

    return (
        <Card className="border-amber-500/50">
            <CardHeader>
                <CardTitle className="text-base">Conditions for your approval</CardTitle>
                <CardDescription>
                    {conditions?.decided_by_name ?? 'The approver'} approved “{step.name}” on these conditions. The request continues only if you accept them.
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
                <p className="whitespace-pre-wrap rounded-md bg-muted/50 px-3 py-2 text-sm">{conditions?.comment}</p>
                <Field>
                    <FieldLabel htmlFor="conditions-comment">Comment <span className="font-normal text-muted-foreground">(required to decline)</span></FieldLabel>
                    <Textarea id="conditions-comment" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
                </Field>
                <div className="flex flex-wrap gap-2">
                    <Button disabled={busy !== null} onClick={() => respond('accept')}>
                        {busy === 'accept' && <Spinner data-icon="inline-start" />}Accept conditions
                    </Button>
                    <Button variant="outline" disabled={busy !== null} onClick={() => respond('decline')}>
                        {busy === 'decline' && <Spinner data-icon="inline-start" />}Decline
                    </Button>
                </div>
            </CardContent>
        </Card>
    )
}

/** Manager-only: withdraw an approval. The reason is mandatory and the action is audited. */
export function RevokeButton({ detail, onDone }: { detail: SubmissionDetail; onDone: () => void }) {
    const [open, setOpen] = useState(false)
    const [reason, setReason] = useState('')
    const [busy, setBusy] = useState(false)
    const s = detail.submission
    if (s.status !== 'approved') return null

    const revoke = async () => {
        setBusy(true)
        try {
            await platformApi.revoke(s.id, reason.trim())
            toast.success('Approval revoked; the request is back in review')
            setOpen(false)
            setReason('')
            onDone()
        } catch (e) {
            failed(e, 'Could not revoke the approval')
        } finally {
            setBusy(false)
        }
    }

    return (
        <>
            <Button size="sm" variant="outline" className="text-destructive" onClick={() => setOpen(true)}>
                <ShieldOff data-icon="inline-start" />Revoke approval
            </Button>
            <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Revoke this approval?</DialogTitle>
                        <DialogDescription>
                            The request goes back to its last approval step for a new decision. The requester and the reviewers are
                            emailed, and your name and reason are recorded permanently in the history.
                        </DialogDescription>
                    </DialogHeader>
                    <Field>
                        <FieldLabel htmlFor="revoke-reason">Reason</FieldLabel>
                        <Textarea id="revoke-reason" rows={4} value={reason} onChange={(e) => setReason(e.target.value)} />
                        <FieldDescription>At least {MIN_REVOKE} characters.</FieldDescription>
                    </Field>
                    <DialogFooter>
                        <Button variant="outline" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
                        <Button variant="destructive" disabled={busy || reason.trim().length < MIN_REVOKE} onClick={revoke}>
                            {busy && <Spinner data-icon="inline-start" />}Revoke approval
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}

/** Manager-only: move a request in review onto a different workflow (e.g. it was routed wrongly). */
export function ChangeWorkflowButton({ detail, onDone }: { detail: SubmissionDetail; onDone: () => void }) {
    const [open, setOpen] = useState(false)
    const [workflows, setWorkflows] = useState<Workflow[]>([])
    const [target, setTarget] = useState('')
    const [reason, setReason] = useState('')
    const [busy, setBusy] = useState(false)
    const s = detail.submission
    useEffect(() => {
        if (!open) return
        platformApi.listWorkflows().then(setWorkflows).catch((e) => failed(e, 'Could not load workflows'))
    }, [open])
    if (s.status !== 'in_review' && s.status !== 'returned') return null

    const choices = workflows.filter((w) => !w.archived && w.id !== detail.workflow?.id)
    const move = async () => {
        setBusy(true)
        try {
            await platformApi.changeWorkflow(s.id, target, reason.trim())
            toast.success('Request moved to the new workflow')
            setOpen(false)
            setTarget('')
            setReason('')
            onDone()
        } catch (e) {
            failed(e, 'Could not change the workflow')
        } finally {
            setBusy(false)
        }
    }

    return (
        <>
            <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
                <Shuffle data-icon="inline-start" />Change workflow
            </Button>
            <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Move to a different workflow</DialogTitle>
                        <DialogDescription>
                            The reviewers are worked out again from this request's answers, and the first step starts. Earlier decisions
                            stay in the history, and the previous reviewers lose access. Your name and reason are recorded.
                        </DialogDescription>
                    </DialogHeader>
                    <Field>
                        <FieldLabel htmlFor="wf-target">New workflow{detail.workflow ? ` (now: ${detail.workflow.name})` : ''}</FieldLabel>
                        <Select value={target || undefined} onValueChange={setTarget}>
                            <SelectTrigger id="wf-target" className="w-full"><SelectValue placeholder="Choose a workflow…" /></SelectTrigger>
                            <SelectContent><SelectGroup>
                                {choices.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                            </SelectGroup></SelectContent>
                        </Select>
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="wf-reason">Reason</FieldLabel>
                        <Textarea id="wf-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
                        <FieldDescription>At least {MIN_REVOKE} characters.</FieldDescription>
                    </Field>
                    <DialogFooter>
                        <Button variant="outline" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
                        <Button disabled={busy || !target || reason.trim().length < MIN_REVOKE} onClick={move}>
                            {busy && <Spinner data-icon="inline-start" />}Move request
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}

/** Comments at any time; reviewers can keep a comment internal (hidden from the requester). */
export function CommentsTab({ detail, isRequester, onDone }: { detail: SubmissionDetail; isRequester: boolean; onDone: () => void }) {
    const [body, setBody] = useState('')
    const [internal, setInternal] = useState(false)
    const [busy, setBusy] = useState(false)
    const s = detail.submission
    const comments = detail.comments ?? []

    const post = async () => {
        setBusy(true)
        try {
            await platformApi.addComment(s.id, body.trim(), internal && !isRequester)
            setBody('')
            onDone()
        } catch (e) {
            failed(e, 'Could not post the comment')
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="flex flex-col gap-4">
            {comments.length === 0 ? (
                <Empty><EmptyHeader><EmptyTitle>No comments yet</EmptyTitle>
                    <EmptyDescription>Use comments to ask a question or add context without making a decision.</EmptyDescription></EmptyHeader></Empty>
            ) : (
                <ul className="flex flex-col gap-2">
                    {comments.map((c) => (
                        <li key={c.id} className="rounded-lg border bg-card px-4 py-3 text-sm">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium">{c.author_name || c.author_id}</span>
                                {c.internal && <span className="text-xs text-muted-foreground">Internal · hidden from the requester</span>}
                                <span className="ml-auto text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
                            </div>
                            <p className="mt-1 whitespace-pre-wrap">{c.body}</p>
                        </li>
                    ))}
                </ul>
            )}
            {s.status !== 'draft' && (
                <div className="flex flex-col gap-3">
                    <Field>
                        <FieldLabel htmlFor="new-comment">Add a comment</FieldLabel>
                        <Textarea id="new-comment" rows={3} value={body} maxLength={5000} onChange={(e) => setBody(e.target.value)} />
                    </Field>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        {isRequester ? <span /> : (
                            <label htmlFor="comment-internal" className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Checkbox id="comment-internal" checked={internal} onCheckedChange={(v) => setInternal(v === true)} />
                                Internal (reviewers and managers only)
                            </label>
                        )}
                        <Button size="sm" disabled={busy || !body.trim()} onClick={post}>
                            {busy && <Spinner data-icon="inline-start" />}Post comment
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
